import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import React, { useMemo, useRef, useState } from 'react';

import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import classNames from 'classnames';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import Image from '@components/shared/Image/Image';
import DesignAwardBadge, { DesignAwardPlace } from '@components/shared/DesignAwards/DesignAwardBadge';
import { imageMaxZoom } from '../MobileImageZoom/zoomHelper';
import ZoomOutIcon from './assets/zoom-out.svg';
import ZoomInIcon from './assets/zoom-in.svg';
import styles from './ImageGrid.module.scss';

interface IImageGridProps {
  className?: string;
  images: {
    id: string;
    uri: string;
    height?: number | null;
    width?: number | null;
  }[];
  altText?: string;
  designAward?: DesignAwardPlace;
}

const ImageGrid = ({
  className = '',
  images,
  altText = 'Product image',
  designAward
}: IImageGridProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [hoveredImageId, setHoveredImageId] = useState<string | null>(null);
  const imageBounds = viewportRef.current && {
    height: viewportRef.current?.scrollHeight,
    width: viewportRef.current?.scrollWidth
  };
  const [clickTimeStamp, setClickTimeStamp] = useState<number | null>(null);
  const formattedImages = useMemo(() => {
    return images.map((image) => {
      const targetImage = {
        height: image.height!,
        width: image.width!
      };

      return {
        ...image,
        maxZoom: (imageBounds && imageMaxZoom(imageBounds, targetImage)) || 2,
        minZoom: 1
      };
    });
  }, [images]);

  return (
    <div className={`${styles.ImageGrid} ${className}`}>
      {formattedImages.map(({
        id,
        uri,
        minZoom,
        maxZoom
      }, idx) => (
        <TransformWrapper
          key={id}
          minScale={minZoom}
          initialScale={minZoom}
          maxScale={maxZoom}
          centerOnInit
          centerZoomedOut
          wheel={{
            disabled: true
          }}
          doubleClick={{
            disabled: true
          }}
        >
          {({
            centerView,
            zoomIn,
            state,
            instance
          }) => {
            const resetZoom = () => {
              centerView(minZoom);
            };

            const handleZoom = (imageId: string, imageUri: string) => {
              analyticsTrack(
                `zoom_clicked`, {
                  imageId,
                  imageUri
                }
              );
              if (state.scale > minZoom) {
                resetZoom();
              } else {
                zoomIn(maxZoom);
              }
            };

            const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
              // Using this logic to prevent zoom when panning
              const timeDelta = e.nativeEvent.timeStamp - clickTimeStamp!;
              if (timeDelta < 500) {
                handleZoom(hoveredImageId!, uri);
              }
            };

            return (
              <div
                className={styles.ImageContainer}
                onMouseLeave={() => {
                  if (!instance.isPanning) {
                    resetZoom();
                  }
                }}
              >
                {minZoom < maxZoom && (
                  <button
                    className={classNames(
                      styles.ZoomButton,
                      { [styles.ImageHovered]: hoveredImageId === id }
                    )}
                    type="button"
                    onClick={() => handleZoom(id, uri)}
                  >
                    {state.scale === maxZoom ? <ZoomOutIcon /> : <ZoomInIcon />}
                  </button>
                )}
                <div
                  ref={viewportRef}
                  onMouseEnter={() => setHoveredImageId(id)}
                  onMouseLeave={() => setHoveredImageId(null)}
                  role="button"
                  tabIndex={0}
                  onMouseDown={(e) => setClickTimeStamp(e.nativeEvent.timeStamp)}
                  onClick={(e) => handleClick(e)}
                  onKeyPress={(e) => handleClick(e)}
                >
                  <TransformComponent>
                    <AspectRatioBox
                      key={id}
                      className={styles.ImageAspectRationWrapper}
                    >
                      { (idx === 0 && !!designAward) && (
                        <DesignAwardBadge
                          place={designAward}
                          className={styles.DesignAwardBadge}
                        />
                      )}
                      <Image
                        className={styles.PdpImage}
                        src={uri}
                        alt={altText ? `${altText} - Image ${idx}` : 'Product'}
                        sizes="600px"
                        fit="fit-in"
                        layout="fill"
                        placeholder="blur"

                      />
                    </AspectRatioBox>
                  </TransformComponent>
                </div>
              </div>
            );
          }}
        </TransformWrapper>
      ))}
    </div>
  );
};

export default ImageGrid;
